import React, { createContext, useState } from 'react'
import { Handler } from 'Types/Context/Modal'
import { WithChildren } from '_metronic/helpers'

import { Modal } from 'react-bootstrap'

interface ModalsContext {
	isOpen: boolean
	isBackdropClick: boolean
	nodeID: string
	size: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen'
	modalNode: React.ReactElement | React.ReactElement[] | null
	setModalNode: React.Dispatch<
		React.SetStateAction<React.ReactElement | React.ReactElement[] | null>
	>
	onPresent: (
		node: React.ReactElement | React.ReactElement[],
		newNodeId: string,
		size: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen',
		isBackdropClick?: boolean
	) => void
	onDismiss: Handler
	customCallback?: () => void
	setCustomCallback: (customCallback: () => void) => void
}

export const ModalContext = createContext<ModalsContext>({
	isOpen: false,
	isBackdropClick: true,
	size: 'md',
	nodeID: '',
	modalNode: null,
	setModalNode: () => null,
	onPresent: (
		node: React.ReactElement | React.ReactElement[],
		newNodeId: string,
		size: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen',
		isBackdropClick?: boolean
	) => {},
	onDismiss: () => null,
	customCallback: () => null,
	setCustomCallback: (customCallback: () => void) => null,
})

const ModalContextProvider: React.FC<WithChildren> = ({ children }) => {
	const [isOpen, setIsOpen] = useState(false)
	const [nodeID, setNodeID] = useState('')
	const [size, setSize] = useState<'sm' | 'md' | 'lg' | 'xl' | 'fullscreen'>('md')
	const [modalNode, setModalNode] = useState<any>(null)
	const [isBackdropClick, setIsBackdropClick] = useState(true)
	const [customCallback, setCustomCallback] = useState<any>(() => null)

	const handlePresent = (
		node: React.ReactElement | React.ReactElement[],
		newNodeId: string,
		size: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen',
		isBackdropClick?: boolean
	) => {
		setModalNode(node)
		setIsOpen(true)
		setNodeID(newNodeId)
		setSize(size)
		setIsBackdropClick(isBackdropClick === undefined ? true : isBackdropClick)
	}

	const handleDismiss = () => {
		setIsOpen(false)

		if (customCallback) customCallback()

		setTimeout(() => {
			setNodeID('')
			setModalNode(null)
		}, 200)
	}

	return (
		<ModalContext.Provider
			value={{
				isOpen,
				isBackdropClick,
				size,
				nodeID,
				modalNode,
				setModalNode,
				onPresent: handlePresent,
				onDismiss: handleDismiss,
				setCustomCallback: setCustomCallback as any,
			}}>
			{children}

			<Modal
				id={nodeID}
				tabIndex={-1}
				aria-hidden="true"
				dialogClassName={`modal-dialog modal-dialog-centered modal-${size}`}
				show={isOpen}
				onHide={handleDismiss}
				backdrop={isBackdropClick ? undefined : 'static'}>
				<div className="modal-body py-lg-10 px-lg-10">
					{modalNode && React.isValidElement(modalNode) && React.cloneElement(modalNode)}
				</div>
			</Modal>
		</ModalContext.Provider>
	)
}

export default ModalContextProvider
